import React, {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import './ReviewManagerDashboard.scss';
import Grid from '@mui/material/Grid';
import ReviewRateTrendWidget from '../dashboard-widgets/review-rate-trend-widget/ReviewRateTrendWidget';
import EndToEndWidget from '../dashboard-widgets/end-to-end-widget/EndToEndWidget';
import TaggingWidget from '../dashboard-widgets/tagging-widget/TaggingWidget';
import DashboardAssignmentGrid from '../dashboard-assignment-grid/DashboardAssignmentGrid';
import ReviewerProductivityWidget from '../dashboard-widgets/reviewer-productivity-widget/ReviewerProductivityWidget';
import ReviewerProgressWidget from '../dashboard-widgets/reviewer-progress-widget/ReviewerProgressWidget';
import {WidgetSwitcher} from '../dashboard-widgets/widget-switcher/WidgetSwitcher';
import {widgetIdMapping} from '../dashboardUtils';
import {ReactComponent as SuccessIcon} from '../../../../src/assets/images/warning-toast.svg';

import ArrToast from '../../common/arr-toast/ArrToast';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {t} from 'i18next';
import TotalReviewProgressWidget from '../dashboard-widgets/total-review-progress-widget/TotalReviewProgressWidget';
import {
  useAssignmentGridData,
  useDrawAssignmentFromPool,
  useReviewDashboardData,
  useSaveWidgetData,
} from '../../../hooks/dashboard/useDashboard';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';

const taggingMockProps = {
  showInfoIcon: true,
  showSettingsIcon: true,
  showExpandIcon: true,
};
const ReviewManagerDashboard: React.FC = () => {
  const [openWidgetSwitcher, setOpenWidgetSwitcher] = useState(false);

  const [widgetToSwap, setWidgetToSwap] = useState<number | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | ''>('');

  const onReplaceClick = (widgetId: number) => {
    setOpenWidgetSwitcher(true);
    setWidgetToSwap(widgetId);
  };

  const {mutate: drawAssignment} = useDrawAssignmentFromPool();

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const projectId = loginUserData.projectId;
  const securityGroupId = loginUserData.securityGroupId;

  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    isFetching: dashboardFetching,
    isError: dashboardError,
    refetch,
  } = useReviewDashboardData();

  const {
    data,
    refetch: refreshGrid,
    isLoading: isGridLoading,
  } = useAssignmentGridData();

  const {mutateAsync: saveWidgetData} = useSaveWidgetData();

  useEffect(() => {
    if (!dashboardData || dashboardLoading || dashboardFetching) {
      return;
    }
    const widgetIds = dashboardData.userWidgets.filter(
      (wid) => wid.isDefaultWidget
    );
    if (widgetIds.length > 0) {
      const runSequentially = async () => {
        for (const wid of widgetIds) {
          await saveWidgetData({IsActive: true, WidgetID: wid.widgetID});
        }
      };
      runSequentially();
    }
  }, [dashboardData, dashboardFetching, dashboardLoading]);

  const getWidget = (widgetId: number) => {
    const reviewerProductivity = dashboardData?.userWidgets.find(
      (widget) => widget.widgetID === widgetIdMapping.reviewerProductivity
    )?.reviewerProductivity;

    const reviewTagCount = dashboardData?.userWidgets.find(
      (widget) => widget.widgetID === widgetIdMapping.tagging
    )?.reviewTagCount;

    const tagDefault =
      dashboardData?.userWidgets.find(
        (widget) => widget.widgetID === widgetIdMapping.tagging
      )?.isDefaultWidget ?? true;

    const reviewProgressConfig = dashboardData?.userWidgets.find(
      (widget) => widget.widgetID === widgetIdMapping.reviewerProgress
    )?.reviewProgress;

    const reveiewRateTrendDefault =
      dashboardData?.userWidgets.find(
        (widget) => widget.widgetID === widgetIdMapping.rateTrend
      )?.isDefaultWidget ?? true;

    switch (widgetId) {
      case widgetIdMapping.endToEnd:
        return (
          <EndToEndWidget
            onReplaceClick={() => onReplaceClick(widgetIdMapping.endToEnd)}
            key={JSON.stringify({projectId, securityGroupId})}
          />
        );
      case widgetIdMapping.reviewerProductivity:
        return (
          <ReviewerProductivityWidget
            {...taggingMockProps}
            reviewerProductivity={reviewerProductivity}
            onReplaceClick={() =>
              onReplaceClick(widgetIdMapping.reviewerProductivity)
            }
            key={JSON.stringify({projectId, securityGroupId})}
          />
        );
      case widgetIdMapping.tagging:
        return (
          <TaggingWidget
            {...taggingMockProps}
            reviewTagCount={reviewTagCount}
            onReplaceClick={() => onReplaceClick(widgetIdMapping.tagging)}
            tagDefault={tagDefault}
            key={JSON.stringify({projectId, securityGroupId})}
          />
        );
      case widgetIdMapping.rateTrend:
        return (
          <ReviewRateTrendWidget
            onReplaceClick={() => onReplaceClick(widgetIdMapping.rateTrend)}
            reviewerCount={dashboardData?.reviewersCount ?? 0}
            isDefault={reveiewRateTrendDefault}
            key={JSON.stringify({projectId, securityGroupId})}
          />
        );
      case widgetIdMapping.reviewerProgress:
        return (
          <ReviewerProgressWidget
            {...taggingMockProps}
            onReplaceClick={() =>
              onReplaceClick(widgetIdMapping.reviewerProgress)
            }
            reviewProgressConfig={reviewProgressConfig}
            key={JSON.stringify({projectId, securityGroupId})}
          />
        );
      case widgetIdMapping.totalReviewProgress:
        return (
          <TotalReviewProgressWidget
            loading={dashboardLoading || dashboardFetching}
            error={dashboardError}
            onReplaceClick={() =>
              onReplaceClick(widgetIdMapping.totalReviewProgress)
            }
            overallCompletedPercentage={
              dashboardData?.totalAssignmentChartData[0]
                ?.totalCompletionPercentage ?? 0
            }
            overallDistributedPercentage={
              dashboardData?.totalAssignmentChartData[0]
                ?.totalWorkingPercentage ?? 0
            }
            overallNotDistributedPercentage={
              dashboardData?.totalAssignmentChartData[0]
                ?.totalInQueuePercentage ?? 0
            }
            stageProgress={
              dashboardData?.assignmentChartData?.map((stage) => ({
                stageName: stage.classificationLabel ?? '',
                completedPercentage: stage.totalCompletionPercentage ?? 0,
                distributedPercentage: stage.totalWorkingPercentage ?? 0,
                notDistributedPercentage: stage.totalInQueuePercentage ?? 0,
              })) ?? []
            }
            key={JSON.stringify({projectId, securityGroupId})}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box className="review-manager-dashboard">
      <div
        className="widget-section"
        data-testid={`review-manager-dashboard-div`}
      >
        <div
          className="widgets"
          data-testid={`review-manager-dashboard-div-widgets-wrapper`}
        >
          {!dashboardError && (
            <Grid
              container
              spacing={2}
              className={
                dashboardLoading || dashboardFetching
                  ? 'widgets-loading-grid'
                  : ''
              }
            >
              {dashboardLoading || dashboardFetching ? (
                <ArrLoadingSpinner />
              ) : (
                dashboardData?.userWidgets?.map((widget) => {
                  return (
                    <Grid size={{xs: 12, md: 4, lg: 4}} key={widget.widgetID}>
                      {getWidget(widget.widgetID)}
                    </Grid>
                  );
                })
              )}
            </Grid>
          )}
        </div>
      </div>
      <div
        className="grid-section"
        data-testid={`review-manager-dashboard-div-button`}
      >
        <DashboardAssignmentGrid
          assignments={data?.reviewerAssignments ?? []}
          onDrawClick={(assignmentID: number) => {
            drawAssignment(assignmentID, {
              onSuccess: () => refreshGrid(),
            });
          }}
          loading={isGridLoading}
          key={JSON.stringify({projectId, securityGroupId})}
        />
      </div>
      <WidgetSwitcher
        isOpen={openWidgetSwitcher}
        onClose={() => {
          setOpenWidgetSwitcher(false);
        }}
        onSuccess={() => {
          setToastType('success');
          refetch();
        }}
        onFail={() => {
          setToastType('error');
        }}
        currentSelected={widgetToSwap}
        currentOrder={
          dashboardData?.userWidgets?.map((wid) => wid.widgetID) ?? []
        }
        existingWidgetIds={
          dashboardData?.userWidgets?.map((wid) => wid.widgetID) ?? []
        }
      />
      {toastType && (
        <ArrToast
          id="1"
          startIcon={<SuccessIcon />}
          showStartIcon={true}
          title={
            toastType === 'success'
              ? t('dashboard.widgetSwitcher.successMessage')
              : t('dashboard.widgetSwitcher.errorMessage')
          }
          type={toastType}
          duration={5000}
          onClose={() => setToastType('')}
          position="bottom-center"
        />
      )}
    </Box>
  );
};

export default ReviewManagerDashboard;
